import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CommodityReportsService } from './commodity-reports.service';
import {
  CommodityDesk,
  CommodityReport,
  CommodityReportsManifest,
} from './commodity-reports.model';

/**
 * Landing page listing the hosted commodity reports: a cross-desk overview,
 * collapsible desk sections and a filterable grid of per-commodity reports.
 * The manifest is fetched from the Reporting Service; every entry links out
 * to its report URL in a new tab.
 */
@Component({
  selector: 'app-commodity-reports',
  templateUrl: './commodity-reports.component.html',
  styleUrls: ['./commodity-reports.component.scss'],
})
export class CommodityReportsComponent implements OnInit, OnDestroy {
  manifest: CommodityReportsManifest;
  loading = true;
  error = false;
  filter = '';

  private readonly openDesks = new Set<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly service: CommodityReportsService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.error = false;
    this.service
      .getManifest()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (manifest) => {
          this.manifest = manifest;
          this.openDesks.clear();
          // Open the first desk by default.
          if (manifest.deskReports.length > 0) {
            this.openDesks.add(manifest.deskReports[0].id);
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.error = true;
        },
      });
  }

  toggleDesk(desk: CommodityDesk): void {
    if (this.openDesks.has(desk.id)) {
      this.openDesks.delete(desk.id);
    } else {
      this.openDesks.add(desk.id);
    }
  }

  get allOpen(): boolean {
    const desks = this.manifest?.deskReports ?? [];
    return (
      desks.length > 0 && desks.every((desk) => this.openDesks.has(desk.id))
    );
  }

  toggleAll(): void {
    const desks = this.manifest?.deskReports ?? [];
    if (this.allOpen) {
      this.openDesks.clear();
    } else {
      desks.forEach((desk) => this.openDesks.add(desk.id));
    }
  }

  /** A desk is expanded when toggled open, or forced open by an active filter. */
  deskExpanded(desk: CommodityDesk): boolean {
    return !!this.normalisedFilter() || this.openDesks.has(desk.id);
  }

  /** Commodities in a desk that match the current filter. */
  visibleCommodities(desk: CommodityDesk): CommodityReport[] {
    const query = this.normalisedFilter();
    if (!query || this.matchesText(desk.title, query)) {
      return desk.commodityReports;
    }
    return desk.commodityReports.filter((report) =>
      this.matchesText(report.title, query),
    );
  }

  /** Whether a desk should be shown given the current filter. */
  deskVisible(desk: CommodityDesk): boolean {
    if (!this.normalisedFilter()) {
      return true;
    }
    return this.visibleCommodities(desk).length > 0;
  }

  /** Whether any desk has visible content under the current filter. */
  get anyVisible(): boolean {
    const desks = this.manifest?.deskReports ?? [];
    return desks.some((desk) => this.deskVisible(desk));
  }

  get totalCommodities(): number {
    const desks = this.manifest?.deskReports ?? [];
    return desks.reduce((sum, desk) => sum + desk.commodityReports.length, 0);
  }

  deskIcon(desk: CommodityDesk): string {
    return this.service.deskPresentation(desk).icon;
  }

  deskSubtitle(desk: CommodityDesk): string {
    return this.service.deskPresentation(desk).subtitle;
  }

  overviewUrl(): string {
    return this.service.reportUrl(this.manifest.url);
  }

  methodologyUrl(): string {
    return this.service.reportUrl(this.manifest.methodologyUrl);
  }

  deskReportUrl(desk: CommodityDesk): string {
    return this.service.reportUrl(desk.url);
  }

  commodityUrl(report: CommodityReport): string {
    return this.service.reportUrl(report.url);
  }

  private normalisedFilter(): string {
    return this.filter.trim().toLowerCase();
  }

  private matchesText(text: string, query: string): boolean {
    return text.toLowerCase().indexOf(query) !== -1;
  }
}
